import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { map, Observable } from 'rxjs';
import { UserService } from './user.service';

@Injectable({
	providedIn: 'root',
})
export class TerminalAdministrationService {
	constructor(
		private http: HttpClient,
		private userService: UserService,
	) {}

	getUrl(terminalId?: string | null) {
		return `${environment.awsEnvUri}/mes/terminals${terminalId ? `/${terminalId}` : ''}`;
	}

	getTerminals(options: IGetTerminalsOptions = {}): Observable<ITerminals> {
		return this.http
			.get<ITerminals>(this.getUrl(), {
				headers: this.userService.getHeaders(),
			})
			.pipe(
				map((response) => ({
					...response,
					result: options.includeArriveLeaveTerminals ? (response.result ?? []) : (response.result ?? []).filter((terminal) => this.isProductionTerminal(terminal)),
				})),
			);
	}

	getTerminalById(terminalId: string): Observable<ITerminal> {
		return this.http
			.get<{ status: number; result: ITerminal }>(this.getUrl(terminalId), {
				headers: this.userService.getHeaders(),
			})
			.pipe(map((response) => response.result));
	}

	getFirstTerminals(): Observable<ITerminal[]> {
		return this.getTerminals().pipe(
			map((response) => {
				const terminals = response.result ?? [];

				const referencedIds = new Set(terminals.filter((t) => !!t.nextId).map((t) => t.nextId));
				return terminals.filter((t) => !referencedIds.has(t.id));
			}),
		);
	}

	isProductionTerminal(terminal: ITerminal | null | undefined): boolean {
		return Boolean(terminal?.sqlQuery?.trim());
	}

	createTerminal(terminal: IPostTerminalRequest): Observable<ITerminal> {
		const sql = terminal.sqlQuery;
		terminal.sqlQuery = this.base64EncodeUnicode(sql);

		return this.http
			.post<{ result: ITerminal }>(
				this.getUrl(),
				{
					alias: terminal.alias,
					name: terminal.name,
					terminalTemplateId: terminal.terminalTemplateId?.trim().length === 0 ? null : terminal.terminalTemplateId?.trim(),
					workTaskId: terminal.workTaskId?.trim().length === 0 ? null : terminal.workTaskId?.trim(),
					completionFlow: terminal.completionFlow,
					completionGroupType: terminal.completionGroupType,
					stationFilter: terminal.stationFilter,
					workTaskCategories: terminal.workTaskCategories,
					incidentCodeGroups: terminal.incidentCodeGroups,
					sqlQuery: terminal.sqlQuery,
					nextId: terminal.nextId,
					printConfigurations: terminal.printConfigurations,
				} as IPostTerminalRequest,
				{
					headers: this.userService.getHeaders(),
				},
			)
			.pipe(map((response) => response.result));
	}

	updateTerminal(terminal: IPutTerminalRequest): Observable<ITerminal> {
		const sql = terminal.sqlQuery;
		terminal.sqlQuery = this.base64EncodeUnicode(sql);

		return this.http
			.put<{ statusCode: number; result: ITerminal }>(
				this.getUrl(terminal.id),
				{
					id: terminal.id,
					alias: terminal.alias,
					name: terminal.name,
					terminalTemplateId: terminal.terminalTemplateId?.trim().length === 0 ? null : terminal.terminalTemplateId?.trim(),
					workTaskId: terminal.workTaskId?.trim().length === 0 ? null : terminal.workTaskId?.trim(),
					completionFlow: terminal.completionFlow,
					completionGroupType: terminal.completionGroupType,
					stationFilter: terminal.stationFilter,
					workTaskCategories: terminal.workTaskCategories,
					incidentCodeGroups: terminal.incidentCodeGroups,
					sqlQuery: terminal.sqlQuery,
					nextId: terminal.nextId,
					printConfigurations: terminal.printConfigurations,
				} as IPutTerminalRequest,
				{ headers: this.userService.getHeaders() },
			)
			.pipe(map((response) => response.result));
	}

	base64EncodeUnicode(str: string): string {
		const utf8Bytes = new TextEncoder().encode(str);
		let binary = '';
		utf8Bytes.forEach((byte) => {
			binary += String.fromCharCode(byte);
		});
		return btoa(binary);
	}

	deleteTerminalById(terminalId: string): Observable<any> {
		return this.http.delete(this.getUrl(terminalId), {
			headers: this.userService.getHeaders(),
		});
	}
}

export interface ITerminals {
	result: ITerminal[];
}
export interface IGetTerminalsOptions {
	includeArriveLeaveTerminals?: boolean;
}
export interface IPutTerminalRequest extends IInternalTerminal {
	id: string;
}
export interface IPostTerminalRequest extends IInternalTerminal {}
export interface ITerminal extends IInternalTerminal {
	id: string | null;
}

interface IInternalTerminal {
	alias: string;
	name: string;
	workTaskId: string | null; // TODO? - should this be removed completely? If so, remove from backend as well
	terminalTemplateId: string | null;
	stationFilter: string | null;
	completionFlow: ICompletionFlow | null;
	completionGroupType: ICompletionGroupType;
	sqlQuery: string | null;
	workTaskCategories: string[];
	incidentCodeGroups: string[];
	nextId: string | null;
	printConfigurations: IPrintConfiguration[];
}

export enum ICompletionFlow {
	ItemCompletionOnStartFlow = 'ItemCompletionOnStartFlow',
	GroupCompletionOnStartFlow = 'GroupCompletionOnStartFlow',
}

export enum ICompletionGroupType {
	Groups = 'Groups',
	Locations = 'Locations',
}

export interface IPrintConfiguration {
	name: string;
	description: string;
	autoPrint: AutoPrintSwitch;
	dataSource: DataSourceTypes;
	printerId: string;
	templateId: string;
	stationNumber: number | null;
}

export enum AutoPrintSwitch {
	None = 'None',
	Always = 'Always',
}

export enum DataSourceTypes {
	Group = 'Group',
	Item = 'Item',
}
